import {
  X,
  Edit2,
  Building2,
  Globe,
  AlertTriangle,
  CheckCircle2,
  Tag,
  Calendar,
  RefreshCw,
  User,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import type { KbEntry } from '@/hooks/useKnowledgeBase';
import { sourceTypeMeta, isFirmSpecific } from '../kbDisplay';

export interface KbEntryDetailProps {
  entry: KbEntry;
  onClose: () => void;
  onEdit?: (entry: KbEntry) => void;
  className?: string;
}

function formatDate(value: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Read-only detail view of a knowledge base entry. Shows the full question
 * and response plus provenance/usage metadata; never mutates the entry.
 */
export function KbEntryDetail({ entry, onClose, onEdit, className }: KbEntryDetailProps) {
  const source = sourceTypeMeta(entry.source_type);
  const firmScoped = isFirmSpecific(entry);

  return (
    <Card className={cn('max-w-3xl w-full', className)}>
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-3">
          <CardTitle className="text-base leading-snug">{entry.question_text}</CardTitle>
          <div className="flex items-center gap-1 shrink-0">
            {onEdit && (
              <Button variant="outline" size="sm" onClick={() => onEdit(entry)}>
                <Edit2 className="h-3.5 w-3.5 mr-1" />
                Edit
              </Button>
            )}
            <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap pt-1">
          <span
            className={cn(
              'inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium',
              source.badgeClass
            )}
          >
            {source.label}
          </span>
          {firmScoped ? (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Building2 className="h-3 w-3" />
              Firm #{entry.firm_id}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <Globe className="h-3 w-3" />
              Global
            </span>
          )}
          {entry.is_stale ? (
            <span className="inline-flex items-center gap-1 text-xs text-amber-600">
              <AlertTriangle className="h-3 w-3" />
              Stale
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 text-xs text-green-600">
              <CheckCircle2 className="h-3 w-3" />
              Current
            </span>
          )}
          {entry.category && (
            <span className="text-xs text-muted-foreground">
              Category: <span className="font-medium">{entry.category}</span>
            </span>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Response */}
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-1">Response</div>
          <div className="bg-muted/50 rounded-md p-3 text-sm whitespace-pre-wrap">
            {entry.response_text ?? '—'}
          </div>
        </div>

        {/* Stale reason */}
        {entry.is_stale && entry.stale_reason && (
          <div className="text-xs text-amber-700 bg-amber-100 rounded px-2 py-1">
            <span className="font-medium">Stale reason:</span> {entry.stale_reason}
          </div>
        )}

        {/* Tags */}
        {entry.tags.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Tag className="h-3 w-3 text-muted-foreground" />
            {entry.tags.map((tag) => (
              <Badge key={tag} variant="outline" className="text-xs py-0 px-1.5">
                {tag}
              </Badge>
            ))}
          </div>
        )}

        {/* Usage / provenance */}
        <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground pt-2 border-t">
          <span>Used {entry.usage_count.toLocaleString()} times</span>
          <span>
            Accept rate:{' '}
            {entry.accept_rate != null ? `${Math.round(entry.accept_rate * 100)}%` : '—'}
          </span>
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            Created {formatDate(entry.created_at)}
          </span>
          <span className="flex items-center gap-1">
            <RefreshCw className="h-3 w-3" />
            Updated {formatDate(entry.updated_at)}
          </span>
          <span className="flex items-center gap-1">
            <User className="h-3 w-3" />
            {entry.created_by}
          </span>
          <span>Version {entry.version}</span>
        </div>
      </CardContent>
    </Card>
  );
}
